import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuickstartFlow } from '../../features/quickstart/useQuickstartFlow';
import { QuickstartPhase } from '../../features/quickstart/QuickstartController';
import { EmbeddedBrowserRedirect } from '../../features/quickstart/components/EmbeddedBrowserRedirect';
import { DeviceFallbackScreen } from '../../features/quickstart/components/DeviceFallback/DeviceFallbackScreen';
import { QuickstartLoading } from '../../features/quickstart/components/QuickstartLoading';
import { QuickstartError } from '../../features/quickstart/components/QuickstartError';
import { QuickstartMatchStatus } from '../../features/quickstart/components/QuickstartMatchStatus';
import { QuickstartNotFound } from '../../features/quickstart/components/QuickstartNotFound';
import { QuickstartThankYouRedirect } from '../../features/quickstart/components/QuickstartThankYouRedirect';
import { QuickstartAutoRedirect } from '../../features/quickstart/components/QuickstartAutoRedirect';
import { QuickstartMobileThankYou } from '../../features/quickstart/components/QuickstartMobileThankYou';
import { PlayerWithQuickstart } from '../player/PlayerWithQuickstart';
import { isDebugBuild } from '@playcastdotio/PeerCore';
import { QuickstartDebugOverlay } from '../../features/quickstart/components/QuickstartDebugPage';
import { gamepadStore } from '@playcastdotio/MobileGamepad';
import { SeamlessBackground, useSeamlessBackgroundVisibility } from '../../features/quickstart/components/SeamlessBackground';
import { SeamlessLoadingOverlay } from '../../features/quickstart/components/SeamlessLoadingOverlay';
import { isSeamlessMode, getResolvedCtaUrl, getResolvedOnLoadPixelUrl, isIOSDevice } from '../../features/quickstart/types/theme';
import { useIsMobileDevice } from '../../utility/utility';
import { useIsLandscape, useIsPortraitOrientation } from '../../features/quickstart/utils/responsiveHooks';
import { AnimatedWishlistButton } from '../player/quickstart/AnimatedWishlistButton';


/**
 * Map phases to component keys to prevent same component from animating to itself
 * Only animate when the actual component type changes
 */
function getComponentKey(phase: QuickstartPhase): string {
  switch (phase) {
    case 'page_viewed':
    case 'environment_check':
    case 'fast_start_matchmaking':
    case 'creating_lobby':
    case 'matchmaking':
    case 'match_found':
    case 'joining_broadcast':
      return 'loading';

    case 'embedded_browser_blocked':
      return 'embedded-browser';

    case 'match_ended':
      return 'match-status';

    case 'device_not_supported':
      return 'device-fallback';

    case 'preparing_player':
    case 'playing_afk':
    case 'playing_active':
      return 'player';

    case 'afk_timeout':
    case 'ad_timer_expired':
    case 'wishlist_early_exit':
    case 'thank_you':
      return 'thank-you';

    case 'auto_redirect':
      return 'auto-redirect';

    case 'matchmaking_failed':
    case 'broadcast_join_failed':
    case 'error':
    case 'auth_failed':
      return 'error';

    case 'cta_page':
      return 'cta';

    case 'auth_attempted':
      return 'auth';

    default:
      return phase;
  }
}

export const Quickstart = () => {
  const { quickstart } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasProcessedNestedParamsRef = useRef(false);
  const isMobile = useIsMobileDevice();
  const isLandscape = useIsLandscape();
  const isPortraitOrientation = useIsPortraitOrientation();
  const shouldRotateButton = isLandscape && isPortraitOrientation;

  // Check for debug mode and handle nested params
  // Built debug bundles are not Vite dev servers; use the build's explicit flag.
  const isDebugMode = isDebugBuild() && searchParams.has('debug');
  const debugPhaseParam = searchParams.get('debug');

  // Handle nested query params in debug parameter (e.g., ?debug=?utm_source=discord or ?debug=utm_source=discord)
  // If debug value contains '=' or starts with '?', parse it as additional query params
  useEffect(() => {
    if (!hasProcessedNestedParamsRef.current && debugPhaseParam && (debugPhaseParam.includes('=') || debugPhaseParam.startsWith('?'))) {
      hasProcessedNestedParamsRef.current = true;

      const decodedDebug = decodeURIComponent(debugPhaseParam);
      console.log('[Quickstart] Detected nested params in debug:', decodedDebug);

      // Parse the nested params
      const cleanParamString = decodedDebug.startsWith('?') ? decodedDebug.slice(1) : decodedDebug;
      const nestedParams = new URLSearchParams(cleanParamString);

      // Create new params with nested values merged in
      const newParams = new URLSearchParams(searchParams);
      nestedParams.forEach((value, key) => {
        newParams.set(key, value);
      });

      // Set debug to empty string to keep debug mode but not treat it as a phase
      newParams.set('debug', '');

      console.log('[Quickstart] Rewriting URL with expanded params:', newParams.toString());

      // Update URL
      setSearchParams(newParams, { replace: true });
    }
  }, [debugPhaseParam, searchParams, setSearchParams]);

  const debugPhase =
    isDebugMode && debugPhaseParam && debugPhaseParam !== '' && !debugPhaseParam.includes('=') && !debugPhaseParam.startsWith('?')
      ? (debugPhaseParam as QuickstartPhase)
      : undefined;

  const {
    state,
    controller,
    onFirstInput,
    onWishlistClicked,
    onWishlistCTAClicked,
    onOpenInBrowserClicked,
    onDeviceHandoffRequested,
    onTrackingPixelFired,
    onAuthSuccess,
    onAuthFailure,
    requestBrowserLocks,
    onAfkTimeout,
    onAdTimerExpired,
    onPlayerDisconnect,
    onLoadingTimeout,
    retryMatchmaking,
    getAFKRemainingTime,
    getAdRemainingTime,
    resetInGameIdleTimer,
    getInGameIdleRemainingTime,
    forceInGameIdle,
    debugJumpToPhase,
  } = useQuickstartFlow(quickstart || 'test', debugPhase);

  const themeSlug = state?.theme?.slug;
  const themeConfig = state?.theme?.mobileGamepadConfig;

  // Serialize config to stable string for dependency comparison.
  // Memoized to avoid JSON.stringify on every render.
  const themeConfigKey = useMemo(
    () =>
      themeConfig
        ? JSON.stringify({
            defaultLayout: themeConfig.defaultLayout,
            defaultHiddenButtons: themeConfig.defaultHiddenButtons,
            defaultOpacity: themeConfig.defaultOpacity,
            useCircularButtons: themeConfig.useCircularButtons,
            customButtonIcons: themeConfig.customButtonIcons,
            customButtonSizes: themeConfig.customButtonSizes,
          })
        : '',
    [themeConfig],
  );

  // useEffect (not useLayoutEffect) so this runs AFTER child component effects.
  // MobileGamepadUi subscribes to store changes in its own useEffect (child first),
  // then this parent effect fires setContext, and the subscription catches the change.
  useEffect(() => {
    if (!quickstart) return;
    if (!themeSlug) return;

    const themeDefaults = themeConfigKey ? (JSON.parse(themeConfigKey) as Parameters<typeof gamepadStore.setContext>[1]) : undefined;

    gamepadStore.setContext(themeSlug, themeDefaults);
    console.log(`[Quickstart] Set gamepad context to: ${themeSlug}`, themeDefaults ? '(with theme defaults)' : '(no theme defaults)');

    return () => {
      console.log(`[Quickstart] Clearing gamepad context`);
      gamepadStore.setContext('');
    };
  }, [quickstart, themeSlug, themeConfigKey]);

  // Hooks must be called unconditionally before any early returns
  // Get background visibility for seamless mode (hook always uses phase safely)
  const backgroundVisible = useSeamlessBackgroundVisibility(state?.phase ?? 'page_viewed');

  // Track whether the seamless overlay is mid-fade-out so we can suppress
  // the z-10 content underneath until the fade completes (prevents doubled logos).
  const [seamlessOverlayFading, setSeamlessOverlayFading] = useState(false);
  const [prevShowSeamlessOverlay, setPrevShowSeamlessOverlay] = useState(false);

  // Fire tracking pixel on page load (if configured for this campaign).
  // Uses new Image() so nothing is added to the DOM — completely invisible.
  // Deduped via sessionStorage to prevent double-fires from StrictMode or OAuth returns.
  // The coordinator's enrichThemeWithSlugConfig already applies slug-config URLs
  // and pass-through params to the theme, so getResolvedOnLoadPixelUrl on the enriched
  // theme returns the fully-enriched URL (with dynamic params + UTMs appended).
  const resolvedPixelUrl = state?.theme ? getResolvedOnLoadPixelUrl(state.theme) : undefined;
  useEffect(() => {
    if (!resolvedPixelUrl) return;
    const storageKey = `onload_pixel_fired_${quickstart}`;
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, '1');
    const img = new Image();
    img.src = resolvedPixelUrl;
    onTrackingPixelFired(resolvedPixelUrl);
  }, [resolvedPixelUrl, quickstart, onTrackingPixelFired]);

  // Compute seamless overlay visibility early (safe defaults when state isn't ready)
  // so the derived-state check below can run unconditionally before early returns.
  const seamlessEnabled = state?.theme ? isSeamlessMode(state.theme) : false;
  const componentKey = state ? getComponentKey(state.phase) : 'loading';
  const showSeamlessOverlay = seamlessEnabled && (
    componentKey === 'loading' || state?.phase === 'preparing_player'
  );

  // Synchronous derived state: detect overlay just started fading.
  // setState during render is supported by React for derived state and
  // restarts the render before committing, eliminating the 1-frame flash.
  if (showSeamlessOverlay !== prevShowSeamlessOverlay) {
    setPrevShowSeamlessOverlay(showSeamlessOverlay);
    if (!showSeamlessOverlay && seamlessEnabled) {
      setSeamlessOverlayFading(true);
    }
  }

  // Early returns for loading/error states
  if (!state || !state.themeLoaded) {
    return (
      <div className='flex h-screen items-center justify-center bg-black'>
        <div className='h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent' />
      </div>
    );
  }

  // Show not found page if campaign couldn't be resolved
  if (state.campaignNotFound) {
    return <QuickstartNotFound />;
  }

  // Additional type guard: theme should exist when themeLoaded is true
  // This satisfies TypeScript and avoids non-null assertions
  if (!state.theme) {
    console.error('[Quickstart] Theme is null despite themeLoaded being true');
    return <QuickstartNotFound />;
  }

  // At this point, TypeScript knows state.theme is defined
  const theme = state.theme;
  const hasSlugConfig = !!(state.slugUrlConfig && state.slugUrlConfig.android.ctaUrl);
  const isPlayerMounted = ['preparing_player', 'playing_afk', 'playing_active'].includes(state.phase);

  // Render debug overlay if in debug mode
  const debugOverlay = isDebugMode ? (
    <QuickstartDebugOverlay
      embedded={isPlayerMounted}
      currentPhase={state.phase}
      sessionId={state.sessionId}
      campaignId={quickstart || 'test'}
      onSelectPhase={debugJumpToPhase}
    />
  ) : null;

  // Render content based on phase
  const renderPhaseContent = () => {
    switch (state.phase) {
      case 'page_viewed':
      case 'environment_check':
        // Seamless mode: loading UI handled by the z-20 overlay, not here
        if (seamlessEnabled) return null;
        return (
          <>
            <QuickstartLoading theme={theme} message='Loading...' />
          </>
        );

      case 'embedded_browser_blocked':
        return (
          <>
            <EmbeddedBrowserRedirect theme={theme} environment={state.environment} onOpenInBrowserClicked={onOpenInBrowserClicked} />
          </>
        );

      // Device lock screen — the email/QR handoff. Kept deliberately separate
      // from the thank-you cases below: this screen continues the visitor onto an
      // allowed device (handoff QR / emailed link), while thank-you is
      // end-of-session (store CTA). The two must never share a render path or a
      // QR hook.
      case 'device_not_supported':
        return (
          <>
            <DeviceFallbackScreen
              theme={theme}
              urlSlug={urlSlug}
              detectedDeviceType={
                state.deviceLock?.detectedDeviceType ??
                (isMobile ? (isIOSDevice() ? 'ios' : 'android') : 'desktop')
              }
              persistentId={state.persistentId}
              onHandoffRequested={onDeviceHandoffRequested}
            />
            {debugOverlay}
          </>
        );

      case 'fast_start_matchmaking':
      case 'creating_lobby':
      case 'matchmaking':
        // Seamless mode: loading UI handled by the z-20 overlay, not here
        if (seamlessEnabled) return null;
        return (
          <>
            <QuickstartLoading
              theme={theme}
              message={
                state.phase === 'matchmaking' && state.queuePosition !== undefined
                  ? `In queue — position ${state.queuePosition}`
                  : undefined
              }
            />
            {debugOverlay}
          </>
        );

      case 'match_ended':
        return (
          <>
            <QuickstartMatchStatus
              theme={theme}
              title='Match Ended'
              message={state.matchEndReason}
              onRetry={retryMatchmaking}
            />
            {debugOverlay}
          </>
        );

      case 'matchmaking_failed':
      case 'broadcast_join_failed':
      case 'error': {
        // Retryable v2 failure (MATCH_FAILED canRetry): offer requeue
        if (state.phase === 'matchmaking_failed' && state.matchCanRetry) {
          return (
            <>
              <QuickstartMatchStatus
                theme={theme}
                title='Matchmaking Failed'
                message={state.errorMessage}
                onRetry={retryMatchmaking}
                retryLabel='Try Again'
              />
              {debugOverlay}
            </>
          );
        }
        return (
          <>
            {isMobile ? (
              <QuickstartMobileThankYou
                theme={theme}
                hasSlugConfig={hasSlugConfig}
                onRedirect={() => onWishlistCTAClicked(
                  state.phase === 'matchmaking_failed' ? 'matchmaking_failed' :
                  state.phase === 'broadcast_join_failed' ? 'broadcast_join_failed' :
                  'error'
                )}
              />
            ) : (
              <QuickstartThankYouRedirect
                theme={theme}
                urlSlug={urlSlug}
                hasSlugConfig={hasSlugConfig}
                onRedirect={() => onWishlistCTAClicked(
                  state.phase === 'matchmaking_failed' ? 'matchmaking_failed' :
                  state.phase === 'broadcast_join_failed' ? 'broadcast_join_failed' :
                  'error'
                )}
              />
            )}
          </>
        );
      }

      case 'match_found':
      case 'joining_broadcast':
        // Seamless mode: loading UI handled by the z-20 overlay, not here
        if (seamlessEnabled) return null;
        return (
          <>
            <QuickstartLoading
              theme={theme}
              message={
                state.phase === 'match_found' && state.estimatedSetupTimeSeconds !== undefined
                  ? `Match found — ready in ~${state.estimatedSetupTimeSeconds}s`
                  : 'Connecting to game...'
              }
            />
            {debugOverlay}
          </>
        );

      case 'preparing_player':
      case 'playing_afk':
      case 'playing_active':
        return (
          <>
            <PlayerWithQuickstart
              quickstartMode={true}
              preparing={state.phase === 'preparing_player'}
              afkTimerActive={state.afkTimerActive}
              adTimerActive={state.adTimerActive}
              adTimerSeconds={state.adTimerSeconds}
              afkTimeoutSeconds={state.afkTimeoutSeconds}
              shouldShowPlayNowOverlay={state.shouldShowPlayNowOverlay}
              theme={theme}
              access={state.access}
              onFirstInput={onFirstInput}
              requestBrowserLocks={requestBrowserLocks}
              onWishlistClicked={onWishlistClicked}
              onAfkTimeout={onAfkTimeout}
              onAdTimerExpired={onAdTimerExpired}
              onDisconnect={onPlayerDisconnect}
              isReconnecting={state.isReconnecting}
              connectionRetries={state.connectionRetries}
              isDebugMode={isDebugMode}
              debugSections={[{ id: 'flow', label: 'Flow', content: debugOverlay }]}
              controller={controller}
              getAFKRemainingTime={getAFKRemainingTime}
              getAdRemainingTime={getAdRemainingTime}
              inGameIdleActive={state.inGameIdleActive}
              inGameIdleTimeoutSeconds={state.inGameIdleTimeoutSeconds}
              resetInGameIdleTimer={resetInGameIdleTimer}
              getInGameIdleRemainingTime={getInGameIdleRemainingTime}
              forceInGameIdle={forceInGameIdle}
            />
          </>
        );

      case 'afk_timeout':
      case 'ad_timer_expired':
      case 'wishlist_early_exit':
      case 'thank_you':
        return (
          <>
              {isMobile ? (
                <QuickstartMobileThankYou
                  theme={theme}
                  hasSlugConfig={hasSlugConfig}
                  onRedirect={() => onWishlistCTAClicked('thank_you')}
                />
              ) : (
                <QuickstartThankYouRedirect
                  theme={theme}
                  urlSlug={urlSlug}
                  hasSlugConfig={hasSlugConfig}
                  onRedirect={() => onWishlistCTAClicked('thank_you')}
                />
              )}
          </>
        );

      case 'auto_redirect': {
        const triggerSource =
          state.exitReason === 'loading_timeout' ? 'loading_timeout' :
          state.exitReason === 'afk_timeout' ? 'afk_timer_hit' :
          state.exitReason === 'ad_timer_expired' ? 'maxed_out_game_time' :
          state.exitReason === 'matchmaking_failed' ? 'matchmaking_failed' :
          state.exitReason === 'broadcast_join_failed' ? 'broadcast_join_failed' :
          state.exitReason === 'auth_failed' ? 'auth_failed' :
          'auto_redirect';
        return (
          <>
            <QuickstartAutoRedirect
              theme={theme}
              hasSlugConfig={hasSlugConfig}
              onRedirect={() => onWishlistCTAClicked(triggerSource)}
            />
          </>
        );
      }

      case 'cta_page':
        return (
          <>
            {isMobile ? (
              <QuickstartMobileThankYou
                theme={theme}
                hasSlugConfig={hasSlugConfig}
                onRedirect={() => onWishlistCTAClicked('cta_page')}
              />
            ) : (
              <QuickstartThankYouRedirect
                theme={theme}
                urlSlug={urlSlug}
                hasSlugConfig={hasSlugConfig}
                onRedirect={() => onWishlistCTAClicked('cta_page')}
              />
            )}
          </>
        );

      case 'auth_attempted':
        controller?.onClerkUnavailable();
        return null;

      case 'auth_failed':
        return (
          <>
            {isMobile ? (
              <QuickstartMobileThankYou
                theme={theme}
                hasSlugConfig={hasSlugConfig}
                onRedirect={() => onWishlistCTAClicked('auth_failed')}
              />
            ) : (
              <QuickstartThankYouRedirect
                theme={theme}
                urlSlug={urlSlug}
                hasSlugConfig={hasSlugConfig}
                onRedirect={() => onWishlistCTAClicked('auth_failed')}
              />
            )}
          </>
        );

      default:
        return (
          <>
            <QuickstartError
              theme={theme}
              urlSlug={urlSlug}
              hasSlugConfig={hasSlugConfig}
              message='Unknown state'
              onWishlistCTAClicked={() => onWishlistCTAClicked('error')}
            />
          </>
        );
    }
  };

  // Guard: Prevent rendering quickstart flow if no campaign slug is provided
  // Note: useQuickstartFlow is called with 'test' fallback to satisfy React's rules of hooks,
  // but we prevent rendering here to avoid storage key collisions from the 'test' placeholder.
  // The 'test' theme exists for initialization, but actual rendering requires a valid campaign.
  if (!quickstart) {
    console.error('[Quickstart] No campaign slug provided in URL');
    return <QuickstartNotFound />;
  }

  // At this point, TypeScript knows quickstart is defined
  // Create a const to use in render that TypeScript recognizes as non-nullable
  const urlSlug: string = quickstart;

  return (
    <>
      {/* z-0: Persistent video background for seamless mode */}
      {seamlessEnabled &&
        state.phase !== 'device_not_supported' && (
        <SeamlessBackground theme={theme} phase={state.phase} visible={backgroundVisible} zIndex={0} />
      )}

      {/* z-10: Main content — all phases render here via AnimatePresence.
         In seamless mode, the loading→player crossfade at z-10 is invisible
         because the seamless overlay at z-20 covers it. The player (with gamepad)
         mounts during preparing_player and is fully opaque by the time the
         overlay starts fading out when entering playing_afk.
         While the seamless overlay is fading out, keep this layer hidden so
         logos from the incoming page don't overlap with the fading overlay logos. */}
      <AnimatePresence>
        <motion.div
          key={componentKey}
          className='absolute inset-0 z-10'
          initial={{ opacity: 0 }}
          animate={{ opacity: (showSeamlessOverlay || seamlessOverlayFading) ? 0 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeInOut' }}
        >
          {renderPhaseContent()}
        </motion.div>
      </AnimatePresence>

      {/* z-20: Seamless loading overlay — sits above main content during
         loading phases and preparing_player. When the coordinator transitions
         from preparing_player → playing_afk, this condition becomes false
         and AnimatePresence triggers the exit fade, revealing the player
         (already mounted and opaque at z-10) underneath. */}
      {seamlessEnabled && (
        <AnimatePresence onExitComplete={() => setSeamlessOverlayFading(false)}>
          {showSeamlessOverlay && (
            <motion.div
              key='seamless-loading-overlay'
              className='absolute inset-0'
              style={{ zIndex: 20 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.0, ease: 'easeInOut' }}
            >
              <SeamlessLoadingOverlay theme={theme} phase={state.phase} urlSlug={urlSlug} onLoadingTimeout={onLoadingTimeout} />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* z-25: Wishlist button during seamless loading phases (above overlay).
         Once the overlay fades out at playing_afk, PlayerWithQuickstart's own
         wishlist button takes over at z-10. */}
      {seamlessEnabled && getResolvedCtaUrl(theme) && showSeamlessOverlay &&
        state.phase !== 'page_viewed' && state.phase !== 'environment_check' && (() => {
          const mobilePosition = theme.wishlistButtonPosition?.mobile || 'bottom-right';
          const desktopPosition = theme.wishlistButtonPosition?.desktop || 'top-right';
          const position = isMobile ? mobilePosition : desktopPosition;

          if (theme.useAnimatedWishlistButton) {
            type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
            const rotatedPositionMap: Record<Position, Position> = {
              'top-left': 'top-right',
              'top-right': 'bottom-right',
              'bottom-right': 'bottom-left',
              'bottom-left': 'top-left',
            };
            const buttonPosition = shouldRotateButton ? rotatedPositionMap[position as Position] : position;
            const stylePosition = shouldRotateButton ? position : buttonPosition;

            return (
              <div style={{ zIndex: 25 }} className='pointer-events-auto'>
                <AnimatedWishlistButton
                  position={buttonPosition as Position}
                  stylePosition={stylePosition as Position}
                  isMobile={isMobile}
                  onClick={onWishlistClicked}
                  text={theme.animatedWishlistButtonText || 'Wishlist\nNow'}
                  animationDuration={theme.animatedWishlistButtonSpeed || 1000}
                  shouldRotate={shouldRotateButton}
                />
              </div>
            );
          }

          const positionClasses: Record<string, string> = {
            'top-left': 'left-0 top-0',
            'top-right': 'right-0 top-0',
            'bottom-left': 'left-0 bottom-0',
            'bottom-right': 'right-0 bottom-0',
          };

          const borderRadius: Record<string, string> = {
            'top-left': '0 0 100px 0',
            'top-right': '0 0 0 100px',
            'bottom-left': '0 100px 0 0',
            'bottom-right': '100px 0 0 0',
          };

          const ctaText = theme.inGameCtaText || 'Wishlist!';
          const buttonWidth = isMobile ? 110 : 245;
          const buttonHeight = isMobile ? 65 : 128;

          return (
            <div
              className={`pointer-events-auto fixed ${positionClasses[position] || positionClasses['top-right']} flex items-center justify-center`}
              style={{ zIndex: 25 }}
            >
              <button
                onClick={onWishlistClicked}
                tabIndex={-1}
                className='pointer-events-auto flex flex-shrink-0 flex-col items-center justify-center border-[3px] border-black bg-gradient-to-b from-yellow-400 to-orange-500 shadow-2xl transition-opacity hover:opacity-90'
                style={{
                  borderRadius: borderRadius[position] || borderRadius['top-right'],
                  width: `${buttonWidth}px`,
                  height: `${buttonHeight}px`,
                  gap: isMobile ? '2px' : '5px',
                }}
              >
                <div className='text-center'>
                  <div className={`font-semibold leading-tight text-black ${isMobile ? 'text-[9px]' : 'text-sm'}`}>
                    {isMobile ? `Tap to ${ctaText}` : ctaText}
                  </div>
                </div>
              </button>
            </div>
          );
        })()}


      {/* The player owns the shared panel while mounted; never stack a second overlay. */}
      {!isPlayerMounted && debugOverlay}
    </>
  );
};

